import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet as WalletIcon, ArrowDown, Clock, CheckCircle, Bank, CurrencyDollar } from '@phosphor-icons/react';
import Navigation from '@/components/Navigation';
import { API } from '@/App';
import { toast } from 'sonner';

const Wallet = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/wallet`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setWallet(d); setLoading(false); })
      .catch(() => { toast.error('Failed to load wallet'); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D28D9]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="wallet-title">My Wallet</h1>
        <p className="text-gray-600 mb-8">Track earnings, escrow, and withdrawals</p>

        {/* Balance Card */}
        <div className="dropio-gradient-banner rounded-3xl p-8 text-white mb-8 relative overflow-hidden">
          <WalletIcon size={120} weight="duotone" className="absolute -top-6 -right-6 opacity-15" />
          <p className="text-sm font-semibold uppercase tracking-widest opacity-90 mb-2">Available balance</p>
          <p className="text-5xl font-extrabold mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="wallet-available">
            ${(wallet?.available_balance || 0).toFixed(2)}
          </p>
          <div className="flex gap-3">
            <button className="bg-white text-gray-900 font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 hover:shadow-lg" data-testid="wallet-withdraw">
              <ArrowDown size={16} weight="bold" /> Withdraw
            </button>
            <button className="bg-white/20 backdrop-blur text-white border border-white/30 font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2">
              <Bank size={16} weight="bold" /> Bank Details
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Clock} label="In Escrow" value={`$${(wallet?.pending_escrow || 0).toFixed(2)}`} desc="Pending delivery" />
          <StatCard icon={CheckCircle} label="Released" value={`$${(wallet?.transactions || []).reduce((s, t) => s + t.amount, 0).toFixed(2)}`} desc="Total earned" />
          <StatCard icon={CurrencyDollar} label="Withdrawn" value="$0.00" desc="All-time withdrawals" />
        </div>

        {/* Pending Escrow */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Pending in Escrow</h2>
          {(wallet?.pending_list || []).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No pending escrow. Funds appear here after payment, before delivery confirmation.</p>
          ) : (
            <div className="space-y-2">
              {wallet.pending_list.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-100 rounded-lg" data-testid={`pending-${i}`}>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">From {p.sender_name}</p>
                    <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <p className="text-lg font-bold text-yellow-700">${p.amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Transaction History</h2>
          {(wallet?.transactions || []).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No transactions yet. Your earnings will appear here once deliveries are confirmed.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {wallet.transactions.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-3" data-testid={`txn-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle size={18} weight="fill" className="text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Delivery from {t.sender_name}</p>
                      <p className="text-xs text-gray-500">{t.released_at ? new Date(t.released_at).toLocaleString() : ''}</p>
                    </div>
                  </div>
                  <p className="text-lg font-bold text-green-700">+${t.amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, desc }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5">
    <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
      <Icon size={20} weight="duotone" className="text-[#6D28D9]" />
    </div>
    <p className="text-2xl font-extrabold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</p>
    <p className="text-xs text-gray-500 font-semibold mt-0.5">{label}</p>
    <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
  </div>
);

export default Wallet;
